/**
 * A cut line that follows the walls it runs along: square moves anywhere, a 45 degree move only
 * beside a wall at that angle, and every change of direction costs extra, so it takes the fewest
 * bends that stay inside the band it is given.
 *
 * A shortest-path search over cell corners, with direction as part of the state. Coordinates are
 * cell corners, as everywhere else in this package; cell (x, y) spans [x, x + 1) x [y, y + 1).
 */

import type { CellPoint } from "./grid.ts";

export type Allowed = (x: number, y: number) => boolean;

/** 400,000 corners: 3.6 million states, some 45 MB of search arrays. */
export const MAX_SEARCH_CORNERS = 400_000;

/** Unit moves in the eight compass directions, indexed 0..7 counter-clockwise from east. */
export const MOVES: readonly CellPoint[] = [
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
  { x: -1, y: 1 },
  { x: -1, y: 0 },
  { x: -1, y: -1 },
  { x: 0, y: -1 },
  { x: 1, y: -1 },
];

/**
 * Where a diagonal move is allowed: the cells within reach of a wall at that angle. `d45` holds
 * cells near walls running at about 45 degrees (up and to the right), `d135` near walls at about
 * 135. Elsewhere the line stays square.
 */
export interface DiagonalCells {
  readonly d45: Uint8Array;
  readonly d135: Uint8Array;
  readonly width: number;
  readonly height: number;
}

export function diagonalCells(
  walls: readonly { readonly a: CellPoint; readonly b: CellPoint }[],
  shape: { readonly width: number; readonly height: number },
  reach: number,
  minLength: number,
  toleranceDegrees = 10,
): DiagonalCells {
  const { width, height } = shape;
  const d45 = new Uint8Array(width * height);
  const d135 = new Uint8Array(width * height);
  for (const { a, b } of walls) {
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length < minLength) continue;
    const degrees = ((Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI + 180) % 180;
    const mask = Math.abs(degrees - 45) <= toleranceDegrees ? d45 : Math.abs(degrees - 135) <= toleranceDegrees ? d135 : null;
    if (mask === null) continue;
    const steps = Math.ceil(length * 2);
    for (let k = 0; k <= steps; k++) {
      const cx = Math.floor(a.x + ((b.x - a.x) * k) / steps);
      const cy = Math.floor(a.y + ((b.y - a.y) * k) / steps);
      for (let dy = -reach; dy <= reach; dy++) {
        for (let dx = -reach; dx <= reach; dx++) {
          const x = cx + dx;
          const y = cy + dy;
          if (x >= 0 && y >= 0 && x < width && y < height) mask[y * width + x] = 1;
        }
      }
    }
  }
  return { d45, d135, width, height };
}

class Heap {
  private readonly keys: number[] = [];
  private readonly values: number[] = [];
  get size() {
    return this.keys.length;
  }
  push(key: number, value: number) {
    const { keys, values } = this;
    keys.push(key);
    values.push(value);
    let i = keys.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (keys[parent]! <= keys[i]!) break;
      [keys[parent], keys[i]] = [keys[i]!, keys[parent]!];
      [values[parent], values[i]] = [values[i]!, values[parent]!];
      i = parent;
    }
  }
  pop(): number {
    const { keys, values } = this;
    const top = values[0]!;
    const lastKey = keys.pop()!;
    const lastValue = values.pop()!;
    if (keys.length > 0) {
      keys[0] = lastKey;
      values[0] = lastValue;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < keys.length && keys[l]! < keys[m]!) m = l;
        if (r < keys.length && keys[r]! < keys[m]!) m = r;
        if (m === i) break;
        [keys[m], keys[i]] = [keys[i]!, keys[m]!];
        [values[m], values[i]] = [values[i]!, values[m]!];
        i = m;
      }
    }
    return top;
  }
}

/**
 * The line from `from` to `to` (both cell corners) through the allowed cells, moving only in
 * `directions` and paying `bendCost` cells of length for each change of direction. Returns the
 * corners where it turns, ends included, or null when the band does not connect the two.
 */
export function traceAlongWalls(
  from: CellPoint,
  to: CellPoint,
  allowed: Allowed,
  box: { readonly minX: number; readonly minY: number; readonly maxX: number; readonly maxY: number },
  diagonals: DiagonalCells | null,
  bendCost: number,
): CellPoint[] | null {
  const w = box.maxX - box.minX + 2;
  const h = box.maxY - box.minY + 2;
  const start = { x: Math.round(from.x), y: Math.round(from.y) };
  const goal = { x: Math.round(to.x), y: Math.round(to.y) };
  const inBox = (p: CellPoint) => p.x >= box.minX && p.y >= box.minY && p.x <= box.maxX + 1 && p.y <= box.maxY + 1;
  if (!inBox(start) || !inBox(goal)) return null;

  const cellOk = (x: number, y: number) => x >= box.minX && y >= box.minY && x <= box.maxX && y <= box.maxY && allowed(x, y);
  // A move runs along a cell edge or across one cell. Along an edge, one side has to be allowed
  // (the line may run along the outline of this sheet's own area); across a cell, that cell has to be.
  const canMove = (p: CellPoint, d: CellPoint) => {
    if (d.x !== 0 && d.y !== 0) {
      const x = p.x + Math.min(d.x, 0);
      const y = p.y + Math.min(d.y, 0);
      if (diagonals === null || x < 0 || y < 0 || x >= diagonals.width || y >= diagonals.height) return false;
      const near = d.x === d.y ? diagonals.d45 : diagonals.d135;
      return near[y * diagonals.width + x] === 1 && cellOk(x, y);
    }
    if (d.y === 0) {
      const x = p.x + Math.min(d.x, 0);
      return cellOk(x, p.y - 1) || cellOk(x, p.y);
    }
    const y = p.y + Math.min(d.y, 0);
    return cellOk(p.x - 1, y) || cellOk(p.x, y);
  };

  // The search keeps a cost per corner and heading over the whole box, so a cut whose box is
  // larger than this keeps its staircase rather than allocating without bound.
  if (w * h > MAX_SEARCH_CORNERS) return null;
  const states = w * h * 9;
  const cost = new Float64Array(states).fill(Infinity);
  const parent = new Int32Array(states).fill(-1);
  const node = (p: CellPoint) => (p.y - box.minY) * w + (p.x - box.minX);
  const point = (n: number): CellPoint => ({ x: (n % w) + box.minX, y: Math.floor(n / w) + box.minY });
  // State = node * 9 + heading, where heading 8 means "not yet moving".
  const startState = node(start) * 9 + 8;
  cost[startState] = 0;
  const heap = new Heap();
  heap.push(0, startState);
  const goalNode = node(goal);
  let reached = -1;

  while (heap.size > 0) {
    const state = heap.pop();
    const n = Math.floor(state / 9);
    const heading = state % 9;
    const here = cost[state]!;
    if (n === goalNode) {
      reached = state;
      break;
    }
    const p = point(n);
    for (let d = 0; d < 8; d++) {
      const move = MOVES[d]!;
      if (!canMove(p, move)) continue;
      const q = { x: p.x + move.x, y: p.y + move.y };
      if (!inBox(q)) continue;
      const step = d % 2 === 0 ? 1 : Math.SQRT2;
      const next = node(q) * 9 + d;
      const total = here + step + (heading !== 8 && heading !== d ? bendCost : 0);
      if (total < cost[next]!) {
        cost[next] = total;
        parent[next] = state;
        heap.push(total, next);
      }
    }
  }
  if (reached < 0) return null;

  const path: CellPoint[] = [];
  let lastHeading = -1;
  for (let state = reached; state >= 0; state = parent[state]!) {
    const heading = state % 9;
    if (heading !== lastHeading) path.push(point(Math.floor(state / 9)));
    lastHeading = heading;
    if (state === startState) break;
  }
  path.reverse();
  if (path.length === 0 || path[path.length - 1]!.x !== goal.x || path[path.length - 1]!.y !== goal.y) path.push(goal);
  // The search runs on whole corners; the true ends may sit between them.
  path[0] = from;
  path[path.length - 1] = to;
  return path;
}
